import _ from "underscore";
import CSRF from "./CSRF";
import Language from "./Language";
import Log from "./Log";
import * as Sentry from "@sentry/browser";
import {
    getSessionMessageKey,
    parseErrorBody,
    SessionAction,
    SessionErrorCode,
    TSessionAction,
} from "./session/sessionErrors";
import {
    clearRenewalQueue,
    flushRenewalQueue,
    hasSessionEnded,
    isRenewalRunning,
    markSessionEnded,
    queueUntilRenewed,
    registerRenewalRetry,
} from "./session/sessionState";

/** What endSession can be asked to do: everything except wait for a renewal. */
type TEndSessionAction = Exclude<TSessionAction, typeof SessionAction.RENEWING>;

const EVENTS = [
    "slowResponse",
    "slowerResponse",
    "timeout",
    "requestSent",
    "complete",
    "updateAvailable",
    "abort",
    "error",
];
const SLOW_TIMEOUT_MS = 300;
const VERY_SLOW_TIMEOUT_MS = 2000;
const SLOW_OPERATION_TIMEOUT_MS = 60000;
const LOG_SLOW_MESSAGE_LIMIT = 10000; // Messages with a longer roundtrip than this are logged to Sentry

const XHR_STATE_COMPLETE = 4;

const HTTP_OK = 200;
const HTTP_BAD_REQUEST = 400;
const HTTP_UNAUTHORIZED = 401;
const HTTP_TOO_MANY_REQUESTS = 429;
const HTTP_INTERNAL_SERVER_ERROR = 500;
const HTTP_GATEWAY_TIMEOUT = 504;

const extractServerStatus = (serverStatusResponse) => {
    return {
        ...serverStatusResponse,
        queues: Array.isArray(serverStatusResponse.queues)
            ? serverStatusResponse.queues.map((queueInfo) => {
                  return `signature: ${queueInfo.signature}, queues: ${queueInfo.queues.join(",")}`;
              })
            : [],
    };
};

const getResponseFor = function (methodName, responses) {
    const response = _.find(
        responses,
        (item) => item.class === "methodresult" && item.name === methodName
    );
    return response || responses;
};

const checkForErrors = function (method, responses) {
    const errorResponse = _.find(responses, (item) => item.class === "error");
    if (errorResponse) {
        const e = new Error(
            Language.get(
                "nc_api_error_from_timeedit",
                `${errorResponse.code}, ${errorResponse.message}`,
                method
            )
        );
        Sentry.captureException(e, {
            extra: { response: errorResponse.message },
            tags: { method },
        });
        throw e;
    }
};

let currentlyProcessedMessage;

export type TMessage = {
    method: string;
    parameters: any;
    metadata: {
        system_lang: boolean;
        language: string;
        isLongOperation: boolean;
    };
    id: number;
    time?: number;
};

class MessageDispatcher {
    url: string;
    timeout: number;
    unauthorizedCallback: () => void;
    cache: any;
    eventHandlers: Record<string, ((data?: any) => void)[]>;

    onLogout?: () => void;

    constructor(url: string, timeout: number, unauthorizedCallback: () => void) {
        this.url = url;
        this.timeout = timeout;
        this.unauthorizedCallback = unauthorizedCallback;
        this.cache = {};
        this.eventHandlers = {};
    }

    on(event: string, callback) {
        if (EVENTS.indexOf(event) === -1) {
            throw new Error(`No event called ${event} in MessageDispatcher.`);
        }

        if (!this.eventHandlers.hasOwnProperty(event)) {
            this.eventHandlers[event] = [];
        }
        this.eventHandlers[event].push(callback);
    }

    fire(event: string, data?: any) {
        if (!this.eventHandlers.hasOwnProperty(event)) {
            return;
        }

        this.eventHandlers[event].forEach((callback) => callback(data));
    }

    send(
        name: string,
        parameters: any,
        callback: (res: any) => void,
        isLongOperation = false,
        errorCallback: (status: string, message: TMessage, response?: any) => void = _.noop,
        signal?: AbortSignal
    ) {
        if (!parameters) {
            throw new Error("Messages can not be sent without parameters.");
        }

        if (isRenewalRunning()) {
            // The session is being renewed. Sending now would only produce another request
            // for tecore-server to fail and retry, so queue until the renewal lands.
            queueUntilRenewed(() =>
                this.dispatch(name, parameters, callback, isLongOperation, errorCallback, signal)
            );
            return;
        }

        this.dispatch(name, parameters, callback, isLongOperation, errorCallback, signal);
    }

    /**
     * Sends a message without being queued behind a session renewal. Only `send` and the
     * renewal retry itself call this - the retry has to get through while everything else
     * is queued, because it is the request leading the renewal.
     */
    dispatch(
        name: string,
        parameters: any,
        callback: (res: any) => void,
        isLongOperation = false,
        errorCallback: (status: string, message: TMessage, response?: any) => void = _.noop,
        signal?: AbortSignal,
        isLeadingSessionRenewal = false
    ) {
        if (signal?.aborted || hasSessionEnded()) {
            return;
        }

        const callbackWithSignal = signal
            ? (result: any) => {
                  if (!signal.aborted) {
                      callback(result);
                  }
              }
            : callback;
        const errorCallbackWithSignal: typeof errorCallback = signal
            ? (status, msg, response?) => {
                  if (!signal.aborted) {
                      errorCallback(status, msg, response);
                  }
              }
            : errorCallback;

        if (
            currentlyProcessedMessage &&
            currentlyProcessedMessage.name === name &&
            _.isEqual(currentlyProcessedMessage.parameters, parameters)
        ) {
            throw new Error("Identical API calls may not be sent within the same callback chain.");
        }

        if (!CSRF.hasToken()) {
            const isSecure = process.env.NODE_ENV !== "development";
            const tokenSetResult = CSRF.updateToken(isSecure);
            if (tokenSetResult instanceof Error) {
                Sentry.captureException(tokenSetResult, {
                    extra: { response: "Token setting failed" },
                    tags: { method: name },
                });
            }
        }

        const metadata: {
            system_lang: true;
            language: string;
            isLongOperation: boolean;
            use_extid?: boolean;
        } = {
            system_lang: true,
            language: navigator.languages ? navigator.languages[0] : navigator.language,
            isLongOperation,
        };

        if (name === "exportObjects" || parameters.useExtid === true) {
            // eslint-disable-next-line no-param-reassign
            delete parameters.useExtid;
            metadata.use_extid = true;
        }

        const message: TMessage = {
            method: name,
            parameters,
            metadata,
            id: nextId(),
        };
        const messageCache = this.getMessageCache(message);
        messageCache.callbacks.push(callbackWithSignal);
        if (messageCache.parameters !== null) {
            // Piggybacking on an in-flight request owned by another caller. We can't
            // abort the XHR — they still want it — but we can drop our callback so it
            // doesn't fire after our signal aborts.
            if (signal) {
                signal.addEventListener(
                    "abort",
                    () => {
                        const cache = this.getMessageCache(message);
                        cache.callbacks = cache.callbacks.filter((cb) => cb !== callbackWithSignal);
                    },
                    { once: true }
                );
            }
            return;
        }
        messageCache.parameters = parameters;

        const slowTimeout = isLongOperation
            ? 0
            : setTimeout(() => {
                  Sentry.addBreadcrumb({
                      category: "api",
                      level: "info",
                      message: `API call slow (>${SLOW_TIMEOUT_MS}ms)`,
                      data: { method: name, id: message.id, threshold_ms: SLOW_TIMEOUT_MS },
                  });
                  this.fire("slowResponse");
              }, SLOW_TIMEOUT_MS);
        const verySlowTimeout = isLongOperation
            ? 0
            : setTimeout(() => {
                  Sentry.addBreadcrumb({
                      category: "api",
                      level: "warning",
                      message: `API call very slow (>${VERY_SLOW_TIMEOUT_MS}ms)`,
                      data: {
                          method: name,
                          id: message.id,
                          threshold_ms: VERY_SLOW_TIMEOUT_MS,
                      },
                  });
                  this.fire("slowerResponse", { id: message.id });
              }, VERY_SLOW_TIMEOUT_MS);

        const req = new XMLHttpRequest();
        const self = this;
        let intentionalAbort = false;

        // Only the request leading a renewal can keep the others queued, and only while it
        // is still being told the renewal is running. On any other outcome it has to flush
        // the queue, or those requests would wait for a retry that never comes.
        const flushQueueIfLeading = () => {
            if (isLeadingSessionRenewal && isRenewalRunning()) {
                flushRenewalQueue();
            }
        };

        // Re-sends this exact message once the session looks usable again.
        const resend = (asRenewalLeader: boolean) => () => {
            // Callers that piggybacked on this request are still holding its cache entry,
            // so carry them over to the new attempt.
            const piggybacked = self
                .getMessageCache(message)
                .callbacks.filter((cb) => cb !== callbackWithSignal);

            // Drop the cache entry, or the resend would attach itself to the entry this
            // attempt left behind and never reach the network.
            self.removeMessageCache(message);
            self.dispatch(
                name,
                parameters,
                callback,
                isLongOperation,
                errorCallback,
                signal,
                asRenewalLeader
            );

            const resentCache = self.getMessageCache(message);
            if (resentCache.parameters !== null && piggybacked.length > 0) {
                resentCache.callbacks.push(...piggybacked);
            }
        };

        if (signal) {
            signal.addEventListener(
                "abort",
                () => {
                    const cache = self.getMessageCache(message);
                    cache.callbacks = cache.callbacks.filter((cb) => cb !== callbackWithSignal);
                    if (cache.callbacks.length === 0) {
                        intentionalAbort = true;
                        req.abort();
                    }
                },
                { once: true }
            );
        }

        const logIfSlow = (data) => {
            if (data.response.time && data.response.time.rtt > LOG_SLOW_MESSAGE_LIMIT) {
                const sentryContext = {
                    extra: { id: data.id, parameters: data.message.parameters },
                    tags: { method: name, tenant: this.url },
                    fingerprint: ["Slow", name],
                };
                Sentry.captureMessage("Slow", sentryContext);
            }
        };

        req.onreadystatechange = function () {
            if (req.readyState !== XHR_STATE_COMPLETE) {
                return;
            }
            const completedTime = Date.now();

            clearTimeout(slowTimeout as number);
            clearTimeout(verySlowTimeout as number);

            // Only a 400 or a 401 can say the renewal is still running; those two branches
            // decide for themselves whether to keep the others queued.
            if (req.status !== HTTP_BAD_REQUEST && req.status !== HTTP_UNAUTHORIZED) {
                flushQueueIfLeading();
            }

            const currentMessageCache = self.getMessageCache(message);
            if (req.status === HTTP_OK) {
                if (isRenewalRunning()) {
                    // Something got through, so the session works again. Release whatever
                    // queued up behind the renewal.
                    flushRenewalQueue();
                }
                try {
                    let responseObject = JSON.parse(req.response);

                    responseObject = responseObject.map((res) =>
                        _.extend(res, {
                            time: _.extend(res.time, {
                                rtt: completedTime - (message.time ?? 0),
                            }),
                        })
                    );

                    checkForErrors(name, responseObject);
                    const result = getResponseFor(name, responseObject);
                    currentlyProcessedMessage = message;
                    self.removeMessageCache(message);
                    currentMessageCache.callbacks.forEach((cachedCallback) => {
                        try {
                            cachedCallback(result); // Assume we deal in JSON from TimeEdit.
                        } catch (e) {
                            Sentry.captureException(e, {
                                extra: { response: result },
                                tags: { method: name },
                            });
                            throw e;
                        }
                    });
                    currentlyProcessedMessage = null;
                    logIfSlow({
                        status: req.status,
                        response: result,
                        id: message.id,
                        message,
                    });
                    self.fire("complete", {
                        status: req.status,
                        response: result,
                        id: message.id,
                    });
                    return;
                } catch (e) {
                    Sentry.captureException(e, {
                        extra: { response: req.response },
                        tags: { method: name },
                    });
                    console.error({
                        eventHandlers: self.eventHandlers,
                        error: e,
                        url: self.url,
                        message,
                    });
                }
            }
            self.fire("complete", { status: req.status, id: message.id });

            if (req.status === HTTP_BAD_REQUEST) {
                // The 400 body carries the teserver error code and, for session errors, what
                // to do about it. Older tecore-server versions send the bare message text.
                const error = parseErrorBody(req.response);

                if (error.sessionAction === SessionAction.RENEWING) {
                    self.handleSessionRenewal(isLeadingSessionRenewal, resend);
                } else if (
                    error.sessionAction === SessionAction.RELOAD ||
                    error.sessionAction === SessionAction.TERMINAL
                ) {
                    self.handleSessionEnded(error.sessionAction, error.apiCode);
                } else {
                    // Only an error we cannot do anything about is worth an event. The two
                    // session outcomes report themselves, once, wherever they are decided.
                    Sentry.captureMessage("Bad request", {
                        level: "error",
                        extra: { id: message.id, response: req.response, apiCode: error.apiCode },
                        tags: { method: name, tenant: self.url },
                        fingerprint: ["BadRequest", name],
                    });
                    flushQueueIfLeading();
                    self.fire("error", { method: name, id: message.id });
                    self.removeMessageCache(message);
                    errorCallbackWithSignal("error", message, req.response);
                }
            }

            if (req.status === HTTP_UNAUTHORIZED) {
                // Check if renewal is in progress and handle it
                const error = parseErrorBody(req.response);
                if (error.sessionAction === SessionAction.RENEWING) {
                    self.handleSessionRenewal(isLeadingSessionRenewal, resend);
                    return;
                }

                flushQueueIfLeading();
                self.unauthorizedCallback();
                return;
            }

            if (req.status === HTTP_TOO_MANY_REQUESTS) {
                // Too many requests, API call limit exceeded
                const e = new Error(Language.get("nc_api_error_from_timeedit", req.response, name));
                Sentry.captureException(e, {
                    extra: { response: req.response },
                    tags: { method: message.method },
                });
                throw e;
            }

            if (req.status === HTTP_INTERNAL_SERVER_ERROR) {
                self.removeMessageCache(message);
                const e = new Error("Server error.");
                Sentry.captureException(e, {
                    extra: { response: req.response },
                    tags: { method: message.method },
                });
                currentMessageCache.callbacks.forEach((cb) => cb(e));
            }
            if (req.status === HTTP_GATEWAY_TIMEOUT) {
                self.removeMessageCache(message);
                const e = new Error("Server error.");
                Sentry.captureException(e, {
                    extra: { response: req.response },
                    tags: { method: message.method },
                });
                currentMessageCache.callbacks.forEach((cb) => cb(e));
            }
        };
        req.open("post", `${this.url}${encodeURIComponent(name)}`, true);
        req.withCredentials = true;
        if (this.timeout) {
            req.timeout = this.timeout;
        }
        if (isLongOperation) {
            req.timeout = SLOW_OPERATION_TIMEOUT_MS;
        }
        if (parameters.timeout !== undefined) {
            req.timeout = parameters.timeout;
        }
        const sentryContext = {
            extra: { id: message.id, serverStatus: null },
            tags: { method: name, tenant: this.url },
        };
        req.ontimeout = function () {
            flushQueueIfLeading();
            self.getServerStatus((result) => {
                sentryContext.extra.serverStatus = extractServerStatus(result);
                Sentry.captureMessage("Timeout", {
                    ...sentryContext,
                    fingerprint: ["Timeout", name],
                });
            });
            self.fire("timeout", {
                method: name,
                id: message.id,
                handled: errorCallbackWithSignal !== _.noop,
            });
            self.removeMessageCache(message);
            errorCallbackWithSignal("timeout", message);
        };
        req.onabort = function () {
            flushQueueIfLeading();
            if (!intentionalAbort) {
                Sentry.captureMessage("Abort", sentryContext);
            }
            self.fire("abort", { method: name, id: message.id });
            self.removeMessageCache(message);
            errorCallbackWithSignal("abort", message);
        };
        req.onerror = function () {
            flushQueueIfLeading();
            Sentry.captureMessage("Error", sentryContext);
            self.fire("error", { method: name, id: message.id });
            self.removeMessageCache(message);
            errorCallbackWithSignal("error", message);
        };
        req.setRequestHeader("Accept", "application/json");
        req.setRequestHeader("Content-Type", "application/json");
        req.setRequestHeader("X-CSRF-TOKEN", `${CSRF.getToken()}}`);
        message.time = Date.now();
        req.send(JSON.stringify(_.omit(message, ["id", "time", "method"])));
        self.fire("requestSent", { id: message.id });
    }

    /**
     * tecore-server is still renewing the session. One request leads and keeps retrying
     * until the renewal lands; every other request is queued behind it rather than piling
     * more failing requests onto a session that is already being replaced.
     */
    handleSessionRenewal(isLeading: boolean, resend: (asRenewalLeader: boolean) => () => void) {
        if (isRenewalRunning() && !isLeading) {
            // Another request is leading the renewal - queue behind it.
            queueUntilRenewed(resend(false));
            return;
        }

        // Add breadcrumb info for coming
        Sentry.addBreadcrumb({
            category: "api",
            level: "info",
            message: "Session renewal in progress",
            data: { leading: isLeading },
        });

        const retryDelay = registerRenewalRetry();

        // If we coudln't registrer a retry. (because of too many retries)
        if (retryDelay === null) {
            // Clear session renewal queue.
            clearRenewalQueue();
            // Trigger a hard reload.
            this.handleSessionEnded(SessionAction.RELOAD, SessionErrorCode.NEW_SESSION_FAILED);
            return;
        }

        // Trigger delayed retry.
        setTimeout(resend(true), retryDelay);
    }

    /**
     * The session is gone. Log this (and inform user) through Log service and then take action:
     * either reload (which should sign them back in through SSO) or log them out.
     */
    handleSessionEnded(action: TEndSessionAction, apiCode: number | null) {
        if (hasSessionEnded()) {
            return;
        }
        markSessionEnded();
        clearRenewalQueue();

        Sentry.captureMessage("Session ended", {
            level: apiCode === SessionErrorCode.NEW_SESSION_FAILED ? "error" : "warning",
            extra: { action },
            tags: { tenant: this.url },
            fingerprint: ["SessionEnded", String(apiCode)],
        });

        const messageKey = getSessionMessageKey(apiCode ?? SessionErrorCode.INVALID_SESSION);
        Log.emergency(Language.get(messageKey), {
            actions: [
                {
                    title: Language.get("dialog_ok"),
                    action: () => {
                        switch (action) {
                            case SessionAction.RELOAD:
                                window.location.reload();
                                return;
                            case SessionAction.TERMINAL:
                                // Call SSO logout and destroy token
                                this.onLogout?.();
                                return;
                            default: {
                                const unhandled: never = action;
                                throw new Error(`Unhandled session action: ${unhandled}`);
                            }
                        }
                    },
                },
            ],
        });
    }

    getServerStatus(callback) {
        const req = new XMLHttpRequest();
        req.open("get", `${this.url}status`, true);
        req.onreadystatechange = function () {
            if (req.readyState !== XHR_STATE_COMPLETE) {
                return;
            }
            if (req.status === HTTP_OK) {
                callback(JSON.parse(req.response));
            } else {
                callback({});
            }
        };
        req.send();
    }

    getMessageCache(message: {
        method: string;
        parameters: any;
        metadata: { system_lang: boolean; language: string; isLongOperation: boolean };
        id: number;
    }) {
        if (!this.cache.hasOwnProperty(message.method)) {
            this.cache[message.method] = [];
        }

        let messageCache = _.find(this.cache[message.method], (cacheItem) =>
            _.isEqual(cacheItem.parameters, message.parameters)
        );

        if (!messageCache) {
            messageCache = { parameters: null, callbacks: [] };
            this.cache[message.method].push(messageCache);
        }

        return messageCache;
    }

    removeMessageCache(message: {
        method: string;
        parameters: any;
        metadata: { system_lang: boolean; language: string; isLongOperation: boolean };
        id: number;
    }) {
        this.cache[message.method] = this.cache[message.method].filter(
            (cacheItem) => !_.isEqual(cacheItem.parameters, message.parameters)
        );
    }
}

let _id = 0;

const nextId = () => {
    _id++;
    return _id;
};

export default MessageDispatcher;
